import { h, Component } from 'preact'
import Phrase from '../../libs/translationsUtils/phrase'
import InputGroup from '../ui-components/InputGroup'
import CodeMirror from '../codemirror'
import Icon from '../assets'
import axios from 'axios'
import { TimeRanges, allTimeRanges } from '../widget-points-leaderboard/indexWidget'
import { isInputEl } from '../../libs/preact-entry'

export type UserRole = {
  isMain: boolean,
  label: string,
  name: string
}

interface Content {
  maxResults: number,
  excludeUserRole: Array<string>,
  timeRange: Array<TimeRanges>,
  emptyMessage: string,
  isPreviewEmptyMessage: boolean
}

interface NewData extends Content {
  title: string
}

interface State extends NewData {
  searchText: string,
  inputInFocus: boolean,
  allUserRoles: Array<UserRole>
}

interface Props {
  title: string,
  controlUrl: string,
  onChange: (state: NewData) => void,
  content: Content,
  allUserRoles?: Array<UserRole>
}

export default class LeaderboardPointsConfig extends Component<Props, State> {
  constructor (props: Props) {
    super(props)

    this.state = {
      title: props.title,
      maxResults: props.content.maxResults,
      excludeUserRole: props.content.excludeUserRole,
      timeRange: props.content.timeRange,
      inputInFocus: false,
      searchText: '',
      allUserRoles: props.allUserRoles || [],
      emptyMessage: props.content.emptyMessage,
      isPreviewEmptyMessage: props.content.isPreviewEmptyMessage
    }

    this.onChangeIsPreviewEmptyMessage = this.onChangeIsPreviewEmptyMessage.bind(this)
    this.onChangeEmptyMessage = this.onChangeEmptyMessage.bind(this)
    this.onSearchUpdated = this.onSearchUpdated.bind(this)
    this.selectUserRole = this.selectUserRole.bind(this)
    this.removeExcludedUserRole = this.removeExcludedUserRole.bind(this)
  }

  async componentDidMount () {
    const result = await axios.get('/role/fetchRoles')
    if (result.data) {
      this.setState({
        allUserRoles: result.data
      })
    }
  }

  selectUserRole (userRole: UserRole) {
    const excludeUserRole = [userRole.name].concat(this.state.excludeUserRole)

    this.setState({
      excludeUserRole: excludeUserRole,
      searchText: ''
    }, () => this.onChangeSend())
  }

  removeExcludedUserRole (userRole: UserRole) {
    const newExcludeUserRole = this.state.excludeUserRole.filter(excludedRole => userRole.name !== excludedRole)

    this.setState({
      excludeUserRole: newExcludeUserRole
    }, () => this.onChangeSend())
  }

  onChangeTitle (newTitle: string) {
    this.setState({
      title: newTitle
    }, () => this.onChangeSend())
  }

  onSearchUpdated (newSearchText: string) {
    this.setState({
      searchText: newSearchText.toLowerCase(),
      inputInFocus: true
    })
  }

  onChangeEmptyMessage (newMessage: string) {
    this.setState({
      emptyMessage: newMessage
    }, () => this.onChangeSend())
  }

  onChangeMaxResults (newMaxResults: number) {
    this.setState({
      maxResults: newMaxResults
    }, () => this.onChangeSend())
  }

  onChangeCheckbox (name: TimeRanges, isChecked: boolean) {
    let newTimeRange = new Set(this.state.timeRange)
    if (isChecked) {
      newTimeRange.add(name)
    } else {
      newTimeRange.delete(name)
    }

    this.setState({
      timeRange: allTimeRanges.filter(range => newTimeRange.has(range))
    }, () => this.onChangeSend())
  }

  onChangeIsPreviewEmptyMessage (isChecked: boolean) {
    this.setState({
      isPreviewEmptyMessage: isChecked
    }, () => this.onChangeSend())
  }

  onChangeSend () {
    const newData: NewData = {
      title: this.state.title,
      maxResults: this.state.maxResults,
      excludeUserRole: this.state.excludeUserRole,
      timeRange: this.state.timeRange,
      emptyMessage: this.state.emptyMessage,
      isPreviewEmptyMessage: this.state.isPreviewEmptyMessage
    }

    this.props.onChange(newData)
  }

  render ({ controlUrl }: Props, {
    title,
    maxResults,
    timeRange,
    emptyMessage,
    searchText,
    isPreviewEmptyMessage,
    excludeUserRole,
    inputInFocus
  }: State) {
    const roles = this.state.allUserRoles.filter(role => {
      if (this.state.excludeUserRole.indexOf(role.name) >= 0) return false
      return role.label.toLowerCase().indexOf(this.state.searchText) >= 0
    })

    return (
      <div class='leaderboard-config'>
        <InputGroup
          title='Title'
          type='text'
          onChange={(e: {target: HTMLInputElement}) => { this.onChangeTitle(e.target.value) }}
          value={title} />
        <InputGroup
          title='Maximum amount of users'
          type='number'
          onChange={(e: {target: HTMLInputElement}) => { this.onChangeMaxResults(parseInt(e.target.value, 10)) }}
          value={maxResults}
          min={1} />

        <div class='leaderboard-config__time-range'>
          <label>Time range</label>
          {allTimeRanges.map(item => <InputGroup
            title={<Phrase p={`leaderboard.points.${item}`} />}
            type='checkbox'
            checked={timeRange.indexOf(item) >= 0 ? 'checked' : ''}
            onChangeCheckbox={(e: {target: HTMLInputElement}) => { this.onChangeCheckbox(item, e.target.checked) }}
          />)}
          <p class={`leaderboard-config__time-range-error ${!timeRange.length ? 'leaderboard-config__time-range-error--active' : ''}`}>Please select at least one option</p>
        </div>

        <div className='widget-configuration_info-box'>
          <strong>🤔 Why is my leaderboard empty?  </strong>
          <p>Please make sure that you enabled point system rules in <a target='_blank' href={ `${controlUrl}/settings/pointSystem` } >point system page in control</a>.</p>
        </div>

        <div className='form-group leaderboard-config__user-roles'>
          <label>Exclude user role</label>
          <div>
            {
              excludeUserRole.map(roleName => {
                const userRole = this.state.allUserRoles.filter(({ name }) => name === roleName)[0]
                if (!userRole) return null
                return (
                  <span className='leaderboard-config__user-roles_selected-role'>{userRole.label} <span className='leaderboard-config__user-role-type'> ({userRole.isMain ? 'Main' : 'Custom'})</span>
                    <span
                      className='leaderboard-config__user-roles_selected-role-remove-button'
                      onClick={() => this.removeExcludedUserRole(userRole)}
                    >
                      <Icon icon='close' width={16} height={16} color={'currentColor'} />
                    </span>
                  </span>
                )
              })
            }
          </div>

          <div class='leaderboard-config__user-roles-selection'>
            <input
              title=''
              class='form-control'
              placeholder='Select user role'
              type='text'
              onFocus={() => this.setState({ inputInFocus: true })}
              onBlur={() => setTimeout(() => {
                this.setState({ inputInFocus: false })
              }, 100)}
              onInput={e => {
                const target = e?.target
                if (!isInputEl(target)) return
                this.onSearchUpdated(target.value)
              }}
              value={searchText}
            />

            {
              inputInFocus && <div class='leaderboard-config__user-role-list'>
                {roles.length ? (

                  <ul>
                    {
                      roles.map(role => (
                        <li
                          className='leaderboard-config__user-role'
                          onMouseDown={() => this.selectUserRole(role)}
                        >{role.label}
                          <span className='leaderboard-config__user-role-type'> ({role.isMain ? 'Main' : 'Custom'})</span>
                        </li>
                      ))
                    }
                  </ul>
                ) : <li className='leaderboard-config__user-role_not-found'>No results found</li>
                }
              </div>
            }
          </div>
        </div>

        <div class='leaderboard-config__empty-status'>
          <label>Empty status</label>
          <p class='leaderboard-config__empty-status-description'>Add a message to be shown when there are fewer than 3 users on leaderboard by custom HTML</p>

          <CodeMirror
            defaultValue={emptyMessage}
            onChange={(html: string) => this.onChangeEmptyMessage(html)}
            options={{
              lineNumbers: true,
              mode: 'text/html',
              selectionPointer: true,
              autoRefresh: true,
              autoResize: true
            }}
          />

          <InputGroup
            title='Show empty status in preview'
            type='checkbox'
            checked={isPreviewEmptyMessage ? 'checked' : ''}
            onChangeCheckbox={(e: {target: HTMLInputElement}) => { this.onChangeIsPreviewEmptyMessage(e.target.checked) }}
            value='preview'
          />
        </div>
      </div>
    )
  }
}
